import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";

import { useBidResponseProcessingContext } from "components/bid_module/bid-response/BidResponsesProcessingProvider";
import { CreateBidResponsesModal } from "components/bid_module/bid-response/modals/CreateBidResponsesModal";
import { SendOpportunityToCRM } from "components/integrations/SendOpportunityToCRMButton";
import { CreditAccessWrapper } from "components/paywall/CreditAccessWrapper";
import { PaidTag } from "components/paywall/paywallUtils";
import { BidDetails } from "lib/hooks/api/bid/bids/useBid";
import { useUpdateBids } from "lib/hooks/api/bid/bids/useUpdateBids";
import { useGenerateBuyerActivity } from "lib/hooks/api/buyer_activity/useGenerateBuyerActivity";
import {
  DEFAULT_CUSTOMER_DOCUMENTS_PAGINATION,
  useCustomerDocuments,
} from "lib/hooks/api/customer_documents/useCustomerDocuments";
import { useTriggerBidDocumentUpload } from "lib/hooks/api/customer_documents/useTriggerBidDocumentUpload";
import { useSubscription } from "lib/hooks/subscription";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { useBidOrchestratorStreamContext } from "lib/providers/bid_orchestrator/BidOrchestratorStreamProvider";
import { usePaywall } from "lib/providers/Paywall";
import { routes, routeSegments } from "lib/routes";
import { EventNames, useTracking } from "lib/tracking";
import {
  BidReportType,
  BuyerActivityObjectType,
  CustomerDocumentSortBy,
  CustomerDocumentSortOrder,
  CustomerDocumentTypeFilter,
} from "lib/types/graphQLEnums";
import { pluralise } from "lib/utils";
import { pulseOpacity } from "../../../styles/animations";
import { Flex, Text } from "../../../styles/utility-components";
import Button from "../../ui/button/Button";
import CTACard from "../../ui/cta_card/CTACard";
import BidResponseDonutChart from "../../ui/donut_chart/BidResponseDonutChart";
import TextAreaInput from "../../ui/input/TextAreaInput";
import toast from "../../ui/toast/Toast";
import { updateBidSchema } from "../bid_details_sidebar/useBidUpdateFormSubmit";
import { UploadTenderDocumentModal } from "../bid_documents/UploadTenderDocumentModal";
import DeleteBidsModal from "../DeleteBidsModal";
import TenderDocumentUploader from "../tender_document_uploader/TenderDocumentUploader";
import { useBidOrchestratorViewState } from "./useBidOrchestratorViewState";

import bidResponsesIllustration from "../../../../assets/images/bids/bid_responses_illustration.png";

type BuyerActivitySummary = BidDetails["buyerActivity"];

type BidContentProps = {
  id: string;
  name: string;
  value: number | undefined;
  sourceUrl: string | undefined;
  bidResponses?: BidDetails["bidResponses"];
  createdAt: string | undefined;
  description: string | undefined;
  submissionDate: string | undefined;
  clarificationDeadline: string | undefined;
  startDate: string | undefined;
  awardDate: string | undefined;
  bidReport: BidDetails["bidReport"];
  qualificationReport: BidDetails["qualificationReport"];
  buyerActivitySummary: BuyerActivitySummary;
  hasNotices?: boolean;
};

export default function BidContent({
  id,
  name,
  value,
  createdAt,
  description,
  submissionDate,
  clarificationDeadline,
  startDate,
  awardDate,
  sourceUrl,
  bidResponses,
  bidReport,
  qualificationReport,
  buyerActivitySummary,
  hasNotices,
}: BidContentProps) {
  const navigate = useNavigate();
  const { logEvent } = useTracking();

  const {
    control,
    reset,
    getValues,
    trigger,
    formState: { dirtyFields },
  } = useForm({
    resolver: zodResolver(updateBidSchema.pick({ name: true })),
    defaultValues: { name },
  });

  useEffect(() => {
    reset({ name });
  }, [name, reset]);

  const { mutate: updateBids } = useUpdateBids({
    onSuccess: () => {
      toast.success("Bid title updated successfully");
      reset({}, { keepValues: true });
    },
    onError: () => toast.error("Failed to update bid title"),
  });

  const submitNameUpdate = async () => {
    if (!dirtyFields.name) return;
    const isValid = await trigger("name");
    const name = getValues("name");
    if (isValid) {
      updateBids({
        updateBidsRequest: {
          ids: [id],
          updateData: { name },
        },
      });
    }
  };

  const [isCreateResponsesModalOpen, setIsCreateResponsesModalOpen] = useState(false);
  const [uploadTenderDocumentsModalOpen, setUploadTenderDocumentsModalOpen] = useState(false);
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);

  const subscription = useSubscription();
  const { openPaywallModal } = usePaywall();

  const hasBidAccess = subscription.hasDataTypes("BIDS");

  const [pendingReportType, setPendingReportType] = useState<BidReportType | null>(null);

  const pendingDocumentCount = useRef<number>(0);
  // Set inside the upload modal's onSubmit so `onOpenChange` can distinguish a
  // submit-close from a cancel-close and clear `pendingReportType` on cancel —
  // preventing a later unrelated upload completing on the bid from firing a
  // phantom report dispatch against a stale queued orchestrator.
  const modalSubmittedRef = useRef(false);

  const triggerUpload = useTriggerBidDocumentUpload(id, { skipProcessing: true });

  // queuedBidOrchestrator is a state which tells frontend that bid report is about to be generated, but maybe has not started
  // orchestrator yet because document uploads are still settling
  const { queueBidOrchestrator } = useBidOrchestratorStreamContext();

  const { data: documents } = useCustomerDocuments(
    {
      resourceId: id,
      sortBy: CustomerDocumentSortBy.FileName,
      sortOrder: CustomerDocumentSortOrder.Asc,
      documentType: CustomerDocumentTypeFilter.BidDocument,
    },
    DEFAULT_CUSTOMER_DOCUMENTS_PAGINATION,
  );

  const bidResponsesCount = bidResponses?.length ?? 0;
  const hasBidResponses = bidResponsesCount > 0;

  const { mutate: generateBuyerActivityReport, isLoading: isGeneratingBuyerActivityReport } =
    useGenerateBuyerActivity();

  const { isExtracting } = useBidResponseProcessingContext(id);

  // Provider-driven view state derived from the in-flight orchestrator run: the
  // CTA state for each report button (idle / processing (pre-`*_started`) /
  // streaming / complete, each with a `navigateTo` link so the CTA's click
  // target goes to the correct page in the report generation journey — load
  // screen or actual report page) plus whether the bid title is still being
  // generated.
  const {
    qualificationReport: qualificationCTAState,
    requirementSummary: requirementSummaryCTAState,
    isGeneratingTitle,
  } = useBidOrchestratorViewState(id, name);

  const handleRequirementSummaryReportClick = async () => {
    setPendingReportType(BidReportType.RequirementSummary);
    if (requirementSummaryCTAState.state !== "idle") {
      await navigate({ to: requirementSummaryCTAState.navigateTo });
    } else {
      setUploadTenderDocumentsModalOpen(true);
    }
  };

  const isGeneratingRequirementSummaryReport =
    requirementSummaryCTAState.state === "processing" ||
    requirementSummaryCTAState.state === "streaming";
  const loadingRequirementSummaryReport = isGeneratingRequirementSummaryReport;

  // Qualification CTA loading
  const isGeneratingQualificationReport =
    qualificationCTAState.state === "processing" || qualificationCTAState.state === "streaming";

  return (
    <Container>
      <Header aria-label="Bid details page header">
        <BidPill>
          <UIcon icon="feather" color="sysDestructiveDefault" />
          <Text body color="sysDestructiveDefault">
            Bid
          </Text>
        </BidPill>
        {isGeneratingTitle ? (
          <GeneratingTitle aria-label="Generating bid title">
            Generating title
            <Ellipsis aria-hidden="true">
              <Dot delay={0}>.</Dot>
              <Dot delay={0.2}>.</Dot>
              <Dot delay={0.4}>.</Dot>
            </Ellipsis>
          </GeneratingTitle>
        ) : (
          <TextAreaInput
            fontStyle="h1"
            name="name"
            control={control}
            placeholder="Enter bid title"
            borderless
            required
            onBlurCapture={submitNameUpdate}
          />
        )}
        <HeaderButtons>
          <SendOpportunityToCRM
            allowParagonCrms
            opportunity={{
              guid: id,
              name: name,
              value: value,
              createDate: createdAt ?? "",
              closeDate: submissionDate,
              stotlesRoute: `${routes.bids}/${id}`,
            }}
            bid={{
              description,
              submissionDate,
              clarificationDeadline,
              startDate,
              awardDate,
            }}
          />
          <Button buttonType="secondary" icon="trash01" onClick={() => setIsDiscardModalOpen(true)}>
            Delete
          </Button>
        </HeaderButtons>
      </Header>

      <ContentSection>
        <CTACardsContainer aria-label="Bid content CTA cards">
          <Flex gap={24} flexWrap="wrap" width="100%">
            <CTACard
              title="Historic buyer activity"
              description="Learn more about this buyer to inform your qualification and approach to this deal."
              noIllustration
              buttonProps={
                buyerActivitySummary
                  ? {
                      width: "auto",
                      buttonText: "View report",
                      ariaLabel: "View buyer activity report",
                      onClick: () => navigate({ to: `/bids/${id}/${routeSegments.buyerActivity}` }),
                    }
                  : {
                      buttonText: "Generate report",
                      icon: "plus",
                      onClick: () => {
                        generateBuyerActivityReport({
                          GenerateBuyerActivitySummaryRequest: {
                            objectId: id,
                            objectType: BuyerActivityObjectType.Bid,
                          },
                        });
                      },
                      disabled: isGeneratingBuyerActivityReport || !hasNotices,
                      isLoading: isGeneratingBuyerActivityReport,
                      ariaLabel: "Generate buyer activity report",
                    }
              }
            />

            {bidReport ? (
              <CTACard
                title="Requirement summary"
                description="Understand the bid requirements to qualify this bid smarter, faster, and with confidence."
                noIllustration
                buttonProps={{
                  width: "auto",
                  buttonText: "View summary",
                  ariaLabel: "View summary",
                  onClick: () =>
                    navigate({
                      to: `/bids/${id}/${routeSegments.requirementSummary}/${bidReport.id}`,
                    }),
                }}
              />
            ) : (
              <CreditAccessWrapper
                requiredDataType="BIDS"
                creditType="BID"
                paywallFeatureType="BID_NO_BID_REPORTS"
                strategyPaywall={false}
              >
                <CTACard
                  title="Requirement summary"
                  description="Understand the bid requirements to qualify this bid smarter, faster, and with confidence."
                  noIllustration
                  buttonProps={{
                    width: "auto",
                    buttonText: loadingRequirementSummaryReport ? "Loading" : "Summarise documents",
                    icon: isGeneratingRequirementSummaryReport ? "spinner" : "plus",
                    isLoading: false,
                    ariaLabel: loadingRequirementSummaryReport ? "Loading" : "Summarise documents",
                    // Flag-on: if a run is already producing this report, click
                    // navigates to its loading page or the report itself rather
                    // than firing a duplicate dispatch.
                    onClick: handleRequirementSummaryReportClick,
                  }}
                />
              </CreditAccessWrapper>
            )}

            {qualificationReport ? (
              <CTACard
                title="Qualification report"
                description="Automatically answer your key qualification question using Stotles AI."
                noIllustration
                buttonProps={{
                  width: "auto",
                  buttonText: "View report",
                  ariaLabel: "View qualification report",
                  onClick: () =>
                    navigate({
                      to: `/bids/${id}/${routeSegments.qualificationReport}/${qualificationReport.id}`,
                    }),
                }}
              />
            ) : (
              <CreditAccessWrapper
                requiredDataType="BIDS"
                creditType="BID"
                paywallFeatureType="BID_NO_BID_REPORTS"
                strategyPaywall={false}
              >
                <CTACard
                  title="Qualification report"
                  description="Automatically answer your key qualification question using Stotles AI."
                  noIllustration
                  buttonProps={{
                    width: "auto",
                    buttonText: isGeneratingQualificationReport ? "Loading" : "Generate report",
                    icon: isGeneratingQualificationReport ? "spinner" : "plus",
                    ariaLabel: isGeneratingQualificationReport
                      ? "Loading qualification report"
                      : "Generate qualification report",
                    // If a run is already producing this report, click navigates
                    // to its loading page or the live report rather than firing
                    // a duplicate dispatch.
                    onClick: isGeneratingQualificationReport
                      ? () => navigate({ to: qualificationCTAState.navigateTo })
                      : () => {
                          setPendingReportType(BidReportType.Qualification);
                          setUploadTenderDocumentsModalOpen(true);
                        },
                  }}
                />
              </CreditAccessWrapper>
            )}
          </Flex>
          {hasBidResponses ? (
            <CTACard
              title="Bid responses"
              description={
                <Flex gap={8} alignItems="center">
                  <UIcon icon="helpCircle" size={16} color="sysTextSecondary" />
                  <Text description color="sysTextSecondary">
                    {pluralise(bidResponsesCount, "Question")}
                  </Text>
                </Flex>
              }
              horizontal
              customContent={<BidResponseDonutChart bidResponses={bidResponses ?? []} />}
              buttonProps={{
                buttonText: "Manage & draft responses",
                onClick: () => {
                  void navigate({ to: `/bids/${id}/bid_responses` });
                },
              }}
            />
          ) : (
            <CTACard
              title="Bid responses"
              description={
                <Flex column gap={16}>
                  <Text description color="sysTextSecondary">
                    Use AI-powered analysis to craft winning responses that address all bid
                    requirements and showcase your competitive advantages
                  </Text>
                  {!hasBidAccess && <PaidTag text="Paid feature" />}
                </Flex>
              }
              imageSrc={bidResponsesIllustration}
              imageStyles={{ transform: "scale(0.75) translateY(20%) translateX(5%)" }}
              altText="Bid responses"
              onClick={hasBidAccess ? undefined : () => openPaywallModal("BID_RESPONSES")}
              horizontal
              buttonProps={
                isExtracting
                  ? {
                      buttonText: "Processing documents",
                      isLoading: true,
                      disabled: true,
                      ariaLabel: "Processing documents",
                    }
                  : hasBidAccess
                    ? {
                        buttonText: "Create responses",
                        icon: "plus",
                        onClick: () => {
                          setIsCreateResponsesModalOpen(true);
                        },
                      }
                    : undefined
              }
            />
          )}

          <TenderDocumentUploader
            bidId={id}
            navigateToDocs={() => navigate({ to: `/bids/${id}/tender-documents` })}
            sourceUrl={sourceUrl}
          />
        </CTACardsContainer>
      </ContentSection>

      <CreateBidResponsesModal
        isOpen={isCreateResponsesModalOpen}
        onOpenChange={setIsCreateResponsesModalOpen}
        bidId={id}
        onNavigateToResponses={() => navigate({ to: `/bids/${id}/bid_responses` })}
      />
      <UploadTenderDocumentModal
        bidId={id}
        isOpen={uploadTenderDocumentsModalOpen}
        onOpenChange={(open) => {
          // Modal closing without onSubmit firing means the user cancelled — clear
          // pendingReportType so a later unrelated upload on this bid can't trip
          // the post-upload effect into dispatching a report the user no longer wants.
          if (!open && !modalSubmittedRef.current) {
            setPendingReportType(null);
          }
          setUploadTenderDocumentsModalOpen(open);
        }}
        sourceUrl={sourceUrl}
        existingDocuments={documents}
        onSubmit={(newFiles) => {
          modalSubmittedRef.current = true;
          const existingDocumentIds = documents?.map((d) => d.id) ?? [];
          pendingDocumentCount.current = existingDocumentIds.length + newFiles.length;

          if (pendingReportType) {
            logEvent(EventNames.qualificationReportCreated, {
              "Action type": "Button clicked",
              "Number of documents": pendingDocumentCount.current,
              "Bid id": id,
              "Report type":
                pendingReportType === BidReportType.Qualification
                  ? "Qualification"
                  : "Requirement summary",
            });
            queueBidOrchestrator(id, {
              reportType: pendingReportType,
              expectsUpload: newFiles.length > 0,
              extractBidDetails: false,
              existingDocumentIds,
            });
            if (newFiles.length > 0) {
              triggerUpload(newFiles);
            }
            const routeSegment =
              pendingReportType === BidReportType.Qualification
                ? routeSegments.qualificationReport
                : routeSegments.requirementSummary;
            void navigate({ to: `/bids/${id}/${routeSegment}` });
          }
        }}
        primaryButtonText="Generate report"
      />

      <DeleteBidsModal
        isOpen={isDiscardModalOpen}
        onOpenChange={(open: boolean) => setIsDiscardModalOpen(open)}
        onSuccess={() => {
          void navigate({ to: "/bids" });
        }}
        selectedBidIds={[id]}
      />
    </Container>
  );
}

const Container = styled(Flex)({
  flexDirection: "column",
  flexGrow: 1,
  gap: 48,
  width: "100%",
  overflow: "auto",
  overflowX: "hidden",
  padding: 64,
});

const Header = styled(Flex)({
  flexDirection: "column",
  gap: 24,
});

// Mirrors the borderless h1 `TextAreaInput` used for the editable title so the
// placeholder occupies the same space and typography while the title streams in
// (font values match FONT_STYLE_MAP.h1 and the borderless padding in TextAreaInput).
const GeneratingTitle = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "baseline",
  width: "100%",
  padding: "5px 0px",
  fontSize: 24,
  fontWeight: 600,
  lineHeight: "133%",
  fontFamily: theme.font.fontFamily,
  color: theme.colors.sysTextDefault,
}));

const Ellipsis = styled.span({
  display: "inline-flex",
});

const Dot = styled.span<{ delay: number }>(({ delay }) => ({
  animation: `${pulseOpacity} 1.4s ease-in-out infinite`,
  animationDelay: `${delay}s`,
}));

const HeaderButtons = styled(Flex)({
  gap: 8,
  alignItems: "center",
});

const BidPill = styled(Flex)(({ theme }) => ({
  width: 64,
  height: 32,
  gap: 8,
  justifyContent: "center",
  alignItems: "center",

  borderRadius: 4,
  border: `1px solid ${theme.colors.sysDestructiveDefault}`,
  backgroundColor: theme.colors.sysDestructiveSubtle,
}));

const ContentSection = styled(Flex)({
  flexDirection: "column",
  gap: 24,
});

const CTACardsContainer = styled(Flex)({
  flexDirection: "row",
  flexWrap: "wrap",
  alignItems: "stretch",
  gap: 24,
});
